// MCP Security — Discovered MCP servers (roadmap item 13). MCP_ROADMAP_UI_2026_11_03
//
// The edge spots MCP JSON-RPC traffic (initialize, tools/list, tools/call …)
// on this site's hosts that doesn't go to a registered endpoint — servers
// none of the MCP controls currently protect. Register one to bring it under
// policy.
//
//   GET /mcp/sites/:site_uid/discovered

import React from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { Box, Stack, Typography, Alert, Button, LinearProgress } from "@mui/material";

import V2PageHeader from "../../../../common/V2PageHeader";
import SiteScopeGate from "../../../../common/SiteScopeGate";
import useGlobalSite from "../../../../../hooks/user/useGlobalSite";

import tokens from "./_shared/tokens";
import useMcpQuery from "./_shared/useMcpQuery";
import { primaryButtonStyle, BORDER_COLOR } from "./_shared/buttonStyles";

function fmtAgo(sec) {
  if (!sec) return "—";
  const d = Math.max(0, Math.floor(Date.now() / 1000 - Number(sec)));
  if (d < 3600) return `${Math.max(1, Math.floor(d / 60))}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
}

export default function McpDiscovered() {
  const { effectiveSiteUid } = useGlobalSite();
  const navigate = useNavigate();
  const path = effectiveSiteUid ? `mcp/sites/${effectiveSiteUid}/discovered` : null;
  const { data: resp, error, loading } = useMcpQuery(path, { skip: !path });
  const rows = resp?.data || [];
  const unregistered = rows.filter((r) => !r.registered);

  return (
    <>
      <Helmet title="MCP Security — Discovered" />
      <Stack spacing={3} sx={{ p: 6 }}>
        <V2PageHeader
          title="MCP Security — Discovered servers"
          description="MCP traffic seen on this site that doesn't go to a registered endpoint (last 7 days). None of the MCP controls apply to it until you register it."
        />
        <SiteScopeGate mode="any">
          {() => (
            <>
              {!effectiveSiteUid && <Alert severity="info">Select a site from the header.</Alert>}
              {error && <Alert severity="error">{error}</Alert>}
              {unregistered.length > 0 && (
                <Alert severity="warning">{unregistered.length} unregistered MCP server{unregistered.length > 1 ? "s" : ""} on this site.</Alert>
              )}
              <Box sx={{ background: "#fff", border: `1px solid ${tokens.neutral[200]}`, borderRadius: "8px", overflow: "hidden" }}>
                {loading && <LinearProgress />}
                <Box sx={{ display: "grid", gridTemplateColumns: "minmax(200px,1.4fr) minmax(200px,1.4fr) 80px 110px 110px 150px", gap: "0 12px", px: 2, height: 38, alignItems: "center", fontSize: 12, color: tokens.neutral[700], borderBottom: `1px solid ${BORDER_COLOR}` }}>
                  <span>Host + path</span><span>Methods seen</span><span>Hits</span><span>First seen</span><span>Last seen</span><span />
                </Box>
                {!rows.length && !loading && (
                  <Box sx={{ p: 3, textAlign: "center", color: tokens.neutral[700], fontSize: 13 }}>
                    No unregistered MCP traffic seen on this site in the last 7 days.
                  </Box>
                )}
                {rows.map((r) => (
                  <Box key={`${r.host}${r.path}`} sx={{ display: "grid", gridTemplateColumns: "minmax(200px,1.4fr) minmax(200px,1.4fr) 80px 110px 110px 150px", gap: "0 12px", px: 2, minHeight: 46, py: 1, alignItems: "center", borderBottom: `1px solid ${tokens.neutral[150]}`, fontSize: 13 }}>
                    <span style={{ fontFamily: tokens.fontMono }} title={r.last_user_agent || ""}>{r.host}{r.path}</span>
                    <span style={{ fontFamily: tokens.fontMono, fontSize: 12, color: tokens.neutral[800] }}>{(r.methods || []).join(", ")}</span>
                    <span style={{ fontFamily: tokens.fontMono }}>{r.hits}</span>
                    <span style={{ fontSize: 12, color: tokens.neutral[700] }}>{fmtAgo(r.first_seen)}</span>
                    <span style={{ fontSize: 12, color: tokens.neutral[700] }}>{fmtAgo(r.last_seen)}</span>
                    <span>
                      {r.registered ? (
                        <Typography sx={{ fontSize: 12, color: tokens.neutral[600] }}>registered</Typography>
                      ) : (
                        <Button size="small" sx={primaryButtonStyle}
                          onClick={() => navigate(`/v2/mcp/onboard?endpoint_url=${encodeURIComponent(r.suggested_endpoint.endpoint_url)}&location_pattern=${encodeURIComponent(r.path)}`)}>
                          Register
                        </Button>
                      )}
                    </span>
                  </Box>
                ))}
              </Box>
            </>
          )}
        </SiteScopeGate>
      </Stack>
    </>
  );
}
